import { useState } from "react";
import { useTranslation } from "react-i18next";
import {
  AlertCircle,
  Check,
  ChevronRight,
  Database,
  FileText,
  Globe,
  Loader2,
  Wrench,
} from "lucide-react";
import graftusMark from "@/assets/graftus-g-logo.png";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import { cn } from "@/lib/utils";
import { useToast } from "@/hooks/use-toast";
import { useDocumentDownloadUrl } from "@/hooks/mutations/use-documents";
import {
  formatToolName,
  getToolIntegration,
  isToolDone,
  toDoneLabel,
  type NormalizedToolPart,
} from "./chat-tool-utils";
import { VaultSourcesWidget } from "./VaultSourcesWidget";
import { VaultReadWidget } from "./VaultReadWidget";

const boxClassName =
  "inline-flex items-center gap-1.5 rounded-md border border-border bg-background px-2 py-1 text-xs leading-none text-muted-foreground";

const SIGNALS = ["kpis", "wellbeing", "team_collab", "momentum"] as const;

const SCORE_DOT_CLASS: Record<string, string> = {
  green: "bg-emerald-500",
  yellow: "bg-amber-400",
  red: "bg-rose-500",
};

const TREND_KEYS: Record<string, string> = {
  improving: "tool.trend.improving",
  stable: "tool.trend.stable",
  declining: "tool.trend.declining",
  unknown: "tool.trend.unknown",
};

const iconClassName = "h-3.5 w-3.5 shrink-0 text-muted-foreground/60";

/**
 * The integration a tool reads, as the user knows it: Graftus' own mark for
 * our data, the app's favicon for a CRM or a connected app (same lookup as
 * the Apps page), a neutral icon when the CRM is not known yet.
 */
function ToolIcon({ part }: { part: NormalizedToolPart }) {
  const integration = getToolIntegration(part.toolName, part.output);
  if (integration.kind === "graftus") {
    return <img src={graftusMark} alt="" className="h-3.5 w-3.5 shrink-0 rounded-[3px]" />;
  }
  if (integration.kind === "app") {
    return (
      <img
        src={`https://www.google.com/s2/favicons?domain=${integration.domain}&sz=32`}
        alt=""
        title={integration.name}
        className="h-3.5 w-3.5 shrink-0 rounded-[3px]"
      />
    );
  }
  if (integration.kind === "crm") return <Database className={iconClassName} />;
  if (integration.kind === "web") return <Globe className={iconClassName} />;
  return <Wrench className={iconClassName} />;
}

function asRecord(value: unknown): Record<string, unknown> | null {
  if (!value || typeof value !== "object" || Array.isArray(value)) return null;
  return value as Record<string, unknown>;
}

function formatDate(value: unknown): string | null {
  if (typeof value !== "string" || !value) return null;
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return null;
  return parsed.toLocaleDateString("sv-SE");
}

function getAssessmentSummary(output: unknown) {
  if (!Array.isArray(output)) return null;
  const latest = asRecord(output[0]);
  return {
    count: output.length,
    latestDate: formatDate(latest?.takenAt ?? latest?.createdAt),
  };
}

function getDetailedAssessmentSummary(output: unknown) {
  const session = asRecord(output);
  if (!session) return null;
  const filesRecord = asRecord(session.resultFiles);
  const files = filesRecord ? Object.values(filesRecord) : [];
  const parsedCount = files.filter((f) => {
    const file = asRecord(f);
    return Boolean(file?.parsedAt);
  }).length;
  return { totalFiles: files.length, parsedCount };
}

function getPulseSummary(output: unknown) {
  const pulse = asRecord(output);
  if (!pulse) return null;
  const currentSignals = asRecord(pulse.currentSignals) ?? {};
  const trend =
    typeof pulse.trend === "string" ? pulse.trend : ("unknown" as const);
  const periods = Array.isArray(pulse.periods) ? pulse.periods.length : 0;
  return { currentSignals, trend, periods };
}

/**
 * The download link comes from the tool output verbatim. The model is told to
 * never repeat it in text: a ~250-char tokenized URL does not survive model
 * transcription reliably (a single corrupted character turns into a
 * "Permission denied" download), so this button is the canonical path.
 */
function getActionPlanPdf(output: unknown) {
  const record = asRecord(output);
  if (!record || record.isError === true) return null;
  const downloadUrl = record.downloadUrl;
  if (typeof downloadUrl !== "string" || !downloadUrl) return null;
  return {
    downloadUrl,
    fileName: typeof record.fileName === "string" ? record.fileName : null,
  };
}

function ActionPlanPdfWidget({
  plan,
}: {
  plan: { downloadUrl: string; fileName: string | null };
}) {
  const { t } = useTranslation("intelligence");
  return (
    <div className="mt-1 rounded-md border border-border/70 bg-muted/30 px-2 py-1.5 text-xs leading-tight text-muted-foreground">
      <div className="mb-1 font-medium text-foreground/90">
        {t("tool.actionPlan.title")}
      </div>
      <a
        href={plan.downloadUrl}
        target="_blank"
        rel="noopener noreferrer"
        className="inline-flex items-center gap-1 font-medium text-primary underline underline-offset-2 hover:text-primary/90"
      >
        <FileText className="h-3.5 w-3.5" />
        {t("tool.actionPlan.download")}
      </a>
    </div>
  );
}

interface DocumentHit {
  documentId: string;
  document: string;
  score: number;
}

function getDocumentHits(output: unknown): DocumentHit[] | null {
  const record = asRecord(output);
  const raw = record?.hits;
  if (!Array.isArray(raw)) return null;
  const hits: DocumentHit[] = [];
  for (const entry of raw) {
    const hit = asRecord(entry);
    if (!hit) continue;
    if (typeof hit.documentId !== "string" || typeof hit.document !== "string") continue;
    hits.push({
      documentId: hit.documentId,
      document: hit.document,
      score: typeof hit.score === "number" ? hit.score : 0,
    });
  }
  return hits;
}

/** One row per cited document, with a link to the file it came from. */
function DocumentHitsWidget({
  hits,
  businessId,
}: {
  hits: DocumentHit[];
  businessId?: string;
}) {
  const { t } = useTranslation("intelligence");
  const { toast } = useToast();
  const downloadUrl = useDocumentDownloadUrl();

  if (hits.length === 0) {
    return (
      <div className="mt-1 rounded-md border border-border/70 bg-muted/30 px-2 py-1.5 text-xs text-muted-foreground">
        {t("tool.documents.none")}
      </div>
    );
  }

  async function open(documentId: string) {
    if (!businessId) return;
    // Opened before the await — see the same pattern in SettingsDocumentsView.
    const tab = window.open("", "_blank", "noopener,noreferrer");
    try {
      const result = await downloadUrl.mutateAsync({ businessId, documentId });
      if (tab) tab.location.href = result.downloadUrl;
      else window.location.assign(result.downloadUrl);
    } catch {
      tab?.close();
      toast({ title: t("tool.documents.openFailed"), variant: "destructive" });
    }
  }

  // Deduplicated: the same document can contribute more than one excerpt, and
  // two identical rows read as a bug rather than as two matches.
  const seen = new Set<string>();
  const unique = hits.filter((hit) => {
    if (seen.has(hit.documentId)) return false;
    seen.add(hit.documentId);
    return true;
  });

  return (
    <div className="mt-1 rounded-md border border-border/70 bg-muted/30 px-2 py-1.5 text-xs leading-tight text-muted-foreground">
      <div className="mb-1 font-medium text-foreground/90">
        {t("tool.documents.title")}
      </div>
      <ul className="space-y-0.5">
        {unique.map((hit) => (
          <li key={hit.documentId} className="flex items-center justify-between gap-2">
            <span className="truncate">{hit.document}</span>
            {businessId ? (
              <button
                type="button"
                className="shrink-0 underline underline-offset-2 hover:text-foreground"
                disabled={downloadUrl.isPending}
                onClick={() => void open(hit.documentId)}
              >
                {t("tool.documents.open")}
              </button>
            ) : null}
          </li>
        ))}
      </ul>
    </div>
  );
}

function ToolOutputWidget({
  part,
  businessId,
}: {
  part: NormalizedToolPart;
  businessId?: string;
}) {
  const { t } = useTranslation("intelligence");

  if (!isToolDone(part.state)) return null;

  if (part.state === "output-error") {
    return (
      <div className="mt-1 rounded-md border border-destructive/30 bg-destructive/5 px-2 py-1.5 text-xs text-destructive">
        {t("tool.failed")}
      </div>
    );
  }

  if (part.toolName === "intelligence_search_documents") {
    return <VaultSourcesWidget output={part.output} />;
  }

  if (part.toolName === "intelligence_read_document") {
    return <VaultReadWidget output={part.output} />;
  }

  if (part.toolName === "intelligence_list_assessments") {
    const summary = getAssessmentSummary(part.output);
    if (!summary) return null;
    return (
      <div className="mt-1 rounded-md border border-border/70 bg-muted/30 px-2 py-1.5 text-xs leading-tight text-muted-foreground">
        <div className="font-medium text-foreground/90">
          {t("tool.assessmentCount", { count: summary.count })}
        </div>
        <div>
          {t("tool.latestDate", { date: summary.latestDate ?? t("tool.unknownDate") })}
        </div>
      </div>
    );
  }

  if (part.toolName === "intelligence_get_assessment") {
    const summary = getDetailedAssessmentSummary(part.output);
    if (!summary) return null;
    return (
      <div className="mt-1 rounded-md border border-border/70 bg-muted/30 px-2 py-1.5 text-xs leading-tight text-muted-foreground">
        <div className="font-medium text-foreground/90">{t("tool.detailsTitle")}</div>
        <div>
          {t("tool.filesParsed", { parsed: summary.parsedCount, total: summary.totalFiles })}
        </div>
      </div>
    );
  }

  if (part.toolName === "intelligence_search_documents") {
    const hits = getDocumentHits(part.output);
    if (!hits) return null;
    return <DocumentHitsWidget hits={hits} businessId={businessId} />;
  }

  if (part.toolName === "intelligence_generate_action_plan_pdf") {
    const plan = getActionPlanPdf(part.output);
    if (!plan) return null;
    return <ActionPlanPdfWidget plan={plan} />;
  }

  if (part.toolName === "intelligence_get_pulse_history") {
    const summary = getPulseSummary(part.output);
    if (!summary) return null;
    return (
      <div className="mt-1 rounded-md border border-border/70 bg-muted/30 px-2 py-1.5 text-xs leading-tight text-muted-foreground">
        <div className="mb-1 font-medium text-foreground/90">{t("tool.pulseTitle")}</div>
        <div className="flex items-center gap-1.5">
          {SIGNALS.map((signal) => {
            const score = summary.currentSignals[signal];
            if (typeof score !== "string" || !SCORE_DOT_CLASS[score]) {
              return (
                <span
                  key={signal}
                  className="inline-block h-2.5 w-2.5 rounded-full bg-muted"
                  title={`${signal}: ${t("tool.notAssessed")}`}
                />
              );
            }
            return (
              <span
                key={signal}
                className={cn(
                  "inline-block h-2.5 w-2.5 rounded-full",
                  SCORE_DOT_CLASS[score],
                )}
                title={`${signal}: ${score}`}
              />
            );
          })}
          <span className="ml-1">
            {t(TREND_KEYS[summary.trend] ?? "tool.trend.unknown")}
          </span>
        </div>
        <div className="mt-1">{t("tool.periodsFetched", { count: summary.periods })}</div>
      </div>
    );
  }

  return null;
}

export function ToolCallGroup({
  parts,
  businessId,
}: {
  parts: NormalizedToolPart[];
  businessId?: string;
}) {
  const { t, i18n } = useTranslation("intelligence");
  const [open, setOpen] = useState(false);
  if (parts.length === 0) return null;

  const allDone = parts.every((part) => isToolDone(part.state));
  const active = [...parts].reverse().find((part) => !isToolDone(part.state));

  // Running: "Räknar möten i Upsales"; done: "Räknade möten i Upsales".
  const toolLabel = (name: string, done: boolean) => {
    const label = t(`tool.labels.${name}`, { defaultValue: formatToolName(name) });
    return done ? toDoneLabel(label, i18n.language) : label;
  };

  // The message body is a flex column, which stretches a flex child to the
  // full width (the single-tool pill used to be one). Every branch therefore
  // renders inside a shrink-wrapped block, so the pill is always as wide as
  // its text, whether it holds one tool, several, or one still running.
  if (!allDone && active) {
    return (
      <div className="w-fit max-w-full">
        <div className={boxClassName}>
          <Loader2 className={cn(iconClassName, "animate-spin")} />
          <ToolIcon part={active} />
          <span className="animate-pulse">{toolLabel(active.toolName, false)}</span>
        </div>
      </div>
    );
  }

  if (parts.length === 1) {
    const part = parts[0]!;
    const isError = part.state === "output-error";
    return (
      <div className="w-fit max-w-full">
        <div className={cn(boxClassName, isError && "border-destructive/30 text-destructive")}>
          {isError ? (
            <AlertCircle className="h-3.5 w-3.5 shrink-0 text-destructive/60" />
          ) : (
            <Check className={iconClassName} />
          )}
          <ToolIcon part={part} />
          <span>{toolLabel(part.toolName, true)}</span>
        </div>
        <ToolOutputWidget part={part} businessId={businessId} />
      </div>
    );
  }

  return (
    <Collapsible open={open} onOpenChange={setOpen} className="w-fit max-w-full">
      <div className="flex flex-col overflow-hidden rounded-md border border-border bg-background">
        <CollapsibleTrigger className="inline-flex items-center gap-1.5 px-2 py-1 text-xs leading-none text-muted-foreground transition-colors hover:bg-muted/40 hover:text-foreground">
          <Check className={cn(iconClassName, open && "hidden")} />
          <ChevronRight
            className={cn(
              iconClassName,
              "transition-transform",
              !open && "hidden",
              open && "rotate-90",
            )}
          />
          <span>{t("tool.usedCount", { count: parts.length })}</span>
        </CollapsibleTrigger>
        <CollapsibleContent>
          {parts.map((part) => {
            const isError = part.state === "output-error";
            return (
              <div
                key={part.toolCallId}
                className={cn(
                  "border-t border-border px-2 py-1 text-xs leading-none text-muted-foreground",
                  isError && "text-destructive",
                )}
              >
                <div className="flex items-center gap-1.5">
                  {isError ? (
                    <AlertCircle className="h-3.5 w-3.5 shrink-0 text-destructive/60" />
                  ) : (
                    <ToolIcon part={part} />
                  )}
                  <span>{toolLabel(part.toolName, true)}</span>
                </div>
                <ToolOutputWidget part={part} businessId={businessId} />
              </div>
            );
          })}
        </CollapsibleContent>
      </div>
    </Collapsible>
  );
}
